import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Chip,
  Container,
  Grid,
  CircularProgress,
  Backdrop,
  Stack,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";

/**
 * CandiadteApplyPage is a React component that renders the candidate
 * application page. It fetches job details based on a token and allows
 * the candidate to submit their application.
 *
 * @return {React.Element} The rendered candidate application page.
 */
function CandiadteApplyPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [jd, setJd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [jdId, setJdId] = useState(null);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get("sid") || "Public";

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    resume: null,
  });

  /**
   * Fetches the job details based on the provided token.
   *
   * @return {Promise<void>} A Promise that resolves when the job details are fetched.
   */
  useEffect(() => {
    /**
     * Fetches the job details based on the provided token.
     *
     * @return {Promise<void>} A Promise that resolves when the job details are fetched.
     */
    const fetchJob = async () => {
      try {
        const res = await api.get(`candidate/career/job-detail/${token}/`);
        setJd(res.data);
        setJdId(res.data.jd_id);
      } catch (err) {
        setDialogError(
          <span style={{ color: "#ff4d4d" }}>
            Invalid or expired job link
          </span>
        );
      }
    };

    if (token) {
      fetchJob();
    }
  }, [token]);

  /**
   * Submits the candidate application form.
   *
   * @return {Promise<void>} A Promise that resolves when the form is submitted.
   * @throws {Error} If any mandatory fields are missing or an invalid email is provided.
   */
  const submit = async () => {
    if (
      !form.name ||
      !form.email ||
      !form.mobile ||
      !form.resume
    ) {
      setDialogError(
        <span style={{ color: "#ff4d4d" }}>
          Please fill all the mandatory fields. indicated *
        </span>
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setDialogError(
        <span style={{ color: "#ff4d4d" }}>
          Please enter a valid email address
        </span>
      );
      return;
    }

    setLoading(true);

    const fd = new FormData();
    fd.append("jd_id", jdId);
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("mobile", form.mobile);
    fd.append("resume", form.resume);
    fd.append("source", source);

    try {
      await api.post("/public/apply/", fd);
      setSubmitted(true);
    } catch {
      setDialogError(
        <span style={{ color: "#ff4d4d" }}>
          Application failed. Please try again.
        </span>
      );
    } finally {
      setLoading(false);
      setDialogError("");
    }
  };

  /**
   * Formats the value with a fallback value.
   *
   * @param {string} value - The value to format.
   * @param {string} fallback - The fallback value if the value is falsy or "not specified" or "n/a".
   * @return {{text: string, dim: boolean}} - An object with the formatted text and a boolean indicating if it is dimmed.
   */
  const formatWithFallback = (value, fallback) => {
      if (!value) return { text: fallback, dim: true };

      const v = value.toLowerCase();
      if (v.includes("not specified") || v === "n/a") {
        return { text: fallback, dim: true };
      }

      return { text: value, dim: false };
  };

  /**
   * Formats the job location with a fallback value.
   *
   * @param {string} value - The job location value.
   * @return {{text: string, dim: boolean}} - An object with the formatted job location and a boolean indicating if it is dimmed.
   */
  const job_location = formatWithFallback(jd?.job_location, "Location");

  /**
   * Formats the years of experience with a fallback value.
   *
   * @param {string} value - The years of experience value.
   * @return {{text: string, dim: boolean}} - An object with the formatted years of experience and a boolean indicating if it is dimmed.
   */
  const exp = formatWithFallback(jd?.years_of_experience, "Exp");

  /**
   * Formats the job type with a fallback value.
   *
   * @param {string} value - The job type value.
   * @return {{text: string, dim: boolean}} - An object with the formatted job type and a boolean indicating if it is dimmed.
   */
  const type = formatWithFallback(jd?.job_type, "Job type");

  /**
   * Formats the salary range with a fallback value.
   *
   * @param {string} value - The salary range value.
   * @return {{text: string, dim: boolean}} - An object with the formatted salary range and a boolean indicating if it is dimmed.
   */
  const salary = formatWithFallback(jd?.salary_range, "Salary range");

  if (submitted) {
    return (
      <Box textAlign="center" py={12}>
        <Card sx={{ maxWidth: 500, mx: "auto", p: 5 }}>
          <Typography variant="h4" fontWeight={700}>
            Application Submitted!
          </Typography>
          <Typography mt={2}>
            Our team will get back to you soon.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
    {api.header_required && (
          <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1100,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: api.header_required ? "130px" : "0px",
                  backgroundImage:
                    "url('https://tse4.mm.bing.net/th/id/OIP.tlagmiTgfXWDiUnZiKjlaAHaCg?rs=1&pid=ImgDetMain&o=7&rm=3')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg, rgba(0,0,0,0.7), rgba(0,0,0,0.4))",
                  }}
                />
      
                <Container sx={{ position: "relative", zIndex: 2 }}>
                  <Typography variant="h4" fontWeight={800} color="#fff">
                    ITConnectUS
                  </Typography>
      
                  <Typography
                    sx={{
                      mt: 1,
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-block",
                      borderBottom: "2px solid #fff",
                    }}
                    onClick={() => navigate("/")}
                  >
                    HOME
                  </Typography>
                </Container>
              </Box>
            </Box>
        )}

      <Container maxWidth="xl">
            <Box
              sx={{
                display: "flex",
                gap: 5,
                alignItems: "flex-start",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box sx={{ flex: 7 }}>
                <Card sx={{ p: 6 }}>
                  <Typography variant="h4" fontWeight={800} gutterBottom>
                    {jd?.job_title || "Loading..."}
                  </Typography>
    
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={3}>
                    <Chip label={`📍 ${job_location.text}`} sx={{ color: job_location.dim ? "#9e9e9e" : "#000" }} />
                    <Chip label={`🧠 ${exp.text}`} sx={{ color: exp.dim ? "#9e9e9e" : "#000" }} />
                    <Chip label={`💼 ${type.text}`} sx={{ color: type.dim ? "#9e9e9e" : "#000" }} />
                    <Chip label={`💰 ${salary.text}`} sx={{ color: salary.dim ? "#9e9e9e" : "#000" }} />
                  </Stack>
    
                  {jd?.job_summary && (
                    <Box mt={4}>
                      <Typography variant="h6" fontWeight={700}>
                        About the Role
                      </Typography>
                      <ul>
                        {jd.job_summary.split("\n").map((r, i) => (
                          <li key={i}>
                            <Typography>{r}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}
    
                  {jd?.responsibilities && (
                    <>
                      <Typography variant="h6" fontWeight={700} mt={3}>
                        Responsibilities
                      </Typography>
                      <Box component="ul" sx={{ pl: 3 }}>
                        {jd.responsibilities.split("\n").map((r, i) => (
                          <li key={i}>
                            <Typography variant="body1">{r}</Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
    
                  {jd?.required_qualifications && (
                    <>
                      <Typography variant="h6" fontWeight={700} mt={3}>
                        Required Qualifications
                      </Typography>
                      <Box component="ul" sx={{ pl: 3 }}>
                        {jd.required_qualifications.split("\n").map((r, i) => (
                          <li key={i}>
                            <Typography variant="body1">{r}</Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                  <br />
                    <Typography variant="h5" gutterBottom>
                      If you want to see more job openings, please click{" "}
                      <Button
                          variant="contained"
                          sx={{ textTransform: "none" }}
                          onClick={() => navigate(`/`)}
                      >
                          Here
                      </Button>
                  </Typography>
                </Card>
              </Box>
    
              <Box
                sx={{
                  flex: 3,
                  position: { md: "sticky" },
                  top: { md: 100 },
                  width: "100%",
                }}
              >
                <Card sx={{ p: 5, position: "relative" }}>
                  <Typography variant="h5" fontWeight={700} mb={3}>
                    Apply Now
                  </Typography>
    
                  <TextField
                    fullWidth
                    placeholder="Enter Full Name *"
                    margin="normal"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                  />
    
                  <TextField
                    fullWidth
                    placeholder="Enter Email *"
                    margin="normal"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
    
                  <TextField
                    fullWidth
                    placeholder="Enter Mobile Number *"
                    margin="normal"
                    value={form.mobile}
                    inputProps={{ maxLength: 14 }}
                    onChange={(e) =>
                      setForm({ ...form, mobile: e.target.value })
                    }
                  />
    
                  <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 2, textTransform: 'none' }}
                    >
                        {form.resume ? `📄 ${form.resume.name}` : "Upload Resume *"}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx"
                          onChange={(e) =>
                            setForm({ ...form, resume: e.target.files[0] })
                          }
                        />
                    </Button>
    
                <Grid item xs={12}>
                    <div style={{ width: "100%" }}>
                    {dialogError}
                    </div>
                </Grid>
    
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3 }}
                    onClick={submit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
    
                  {loading && (
                    <Backdrop
                      open
                      sx={{
                        position: "absolute",
                        borderRadius: 2,
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress color="inherit" />
                    </Backdrop>
                  )}
                </Card>
              </Box>
            </Box>
          </Container>

      <Box textAlign="center" py={6} sx={{ opacity: 0.6 }}>
        <Typography variant="body2">
          © {new Date().getFullYear()} {api.app_name}. Built for the future of hiring.
        </Typography>
      </Box>
    </>
  );
}

export default CandiadteApplyPage;